import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
    AppBar,
    Box,
    Button,
    Container,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { keyframes } from "@mui/system";
import { LocalHospitalOutlined, Phone } from '@mui/icons-material';

import { RootState } from '../../../redux/store';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { setLanguage } from '../../../redux/reducer/languageSlice';
import PageLang from '../../../config/page.languge';
import { NAVIGATE_AUTH } from '../../../constant';
import useResponsive from '../../../hooks/useResponsive';
import RegisterDialog from './RegisterDialog';
import ImageWithLoader from '../../common/ImageWithLoader';

const DEFAULT_FONT_SIZE = 16;
const MAX_FONT_SIZE = 20;
const MIN_FONT_SIZE = 12;
const TOLL_FREE_NUMBER = "18001232211";
const TOLL_FREE_TEL_LINK = `tel:${TOLL_FREE_NUMBER}`;

const ringAnimation = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(-10deg); }
  30% { transform: rotate(10deg); }
  45% { transform: rotate(-10deg); }
  60% { transform: rotate(10deg); }
  100% { transform: rotate(0deg); }
`;
export default function ArogyaMitraTopbar() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const language = useSelector((state: RootState) => state.language.language);
    const [fontSize, setFontSize] = React.useState(DEFAULT_FONT_SIZE);
    const { isMobile } = useResponsive()
    const theme = useTheme();

    const [open, setOpen] = React.useState(false);

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    React.useEffect(() => {
        if (!language) {
            dispatch(setLanguage("english"));
        }
    }, [dispatch, language]);

    const handleLanguageChange = (lang: "english" | "marathi") => {
        dispatch(setLanguage(lang));
        dispatch(showSnackbar({ type: "success", message: lang === "marathi" ? "मराठी भाषा निवडली." : "English Language Selected.", }));
    };

    React.useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}px`;
    }, [fontSize]);

    const increaseFontSize = () => setFontSize((prev) => (prev < MAX_FONT_SIZE ? prev + 2 : prev));
    const decreaseFontSize = () => setFontSize((prev) => (prev > MIN_FONT_SIZE ? prev - 2 : prev));
    const resetFontSize = () => setFontSize(DEFAULT_FONT_SIZE);


    return (
        <Box sx={{ zIndex: 101 }}>
            <AppBar position="static" sx={{ backgroundColor: "common.white", boxShadow: "0px 6px 16px rgba(0,0,0,0.10)", zIndex: 101 }} >
                <Toolbar
                    sx={{
                        minHeight: "48px !important",
                        px: { xs: 1, sm: 3 },
                        display: "flex",
                        justifyContent: "space-between",
                        zIndex: 102,
                        backdropFilter: "blur(8px)",
                        background: "linear-gradient(135deg, #053046 0%, #034a6b 50%, #053046 100%)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                >
                    <Container maxWidth="xl" sx={{ display: { xs: "flex", md: "flex" }, minHeight: "35px !important", border: "none", justifyContent: "flex-end", width: "100%", margin: "auto", px: { xs: 2, sm: 3, md: 4 }, alignItems: "center" }}>
                        <ToggleButtonGroup
                            value={language}
                            exclusive
                            size="small"
                            onChange={(_, newLang) => {
                                if (newLang) handleLanguageChange(newLang);
                            }}
                            sx={{
                                borderRadius: "24px",
                                backgroundColor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                padding: "2px",
                                boxShadow: "0px 1px 3px rgba(0,0,0,0.12)",

                                "& .MuiToggleButtonGroup-grouped": {
                                    margin: 0,
                                    border: "none",
                                    borderRadius: "20px !important",
                                },
                            }}
                        >
                            <ToggleButton
                                value="english"
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    px: 1.2,
                                    py: 0.3,
                                    minHeight: "24px",
                                    borderRadius: "20px",

                                    "&.Mui-selected": {
                                        backgroundColor: "primary.main",
                                        color: "#fff",
                                        fontWeight: 600,
                                    },
                                    "&.Mui-selected:hover": {
                                        backgroundColor: "primary.main",
                                    },
                                }}
                            >
                                English
                            </ToggleButton>

                            <ToggleButton
                                value="marathi"
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    px: 1.2,
                                    py: 0.3,
                                    minHeight: "24px",
                                    borderRadius: "20px",

                                    "&.Mui-selected": {
                                        backgroundColor: "primary.main",
                                        color: "#fff",
                                        fontWeight: 600,
                                    },
                                    "&.Mui-selected:hover": {
                                        backgroundColor: "primary.main",
                                    },
                                }}
                            >
                                मराठी
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Box sx={{ display: { xs: "none", md: "flex" } }}>
                            <Button onClick={decreaseFontSize} color='inherit' variant='outlined' sx={{ ml: 1, px: 2, py: 0.7, minWidth: '40px', fontSize: '14px', borderRadius: "24px", }}  >{PageLang?.TopText[language]} -</Button>
                            <Button onClick={resetFontSize} color='inherit' variant='outlined' sx={{ ml: 1, px: 2, py: 0.7, minWidth: '40px', fontSize: '14px', borderRadius: "24px", }}>{PageLang?.TopText[language]} =</Button>
                            <Button onClick={increaseFontSize} color='inherit' variant='outlined' sx={{ ml: 1, px: 2, py: 0.7, minWidth: '40px', fontSize: '14px', borderRadius: "24px", }}>{PageLang?.TopText[language]} +</Button>
                        </Box>
                        <Button
                            endIcon={<LocalHospitalOutlined />}
                            onClick={handleClick}
                            variant="contained"
                            sx={{
                                background: "#fff",
                                color: "primary.main",
                                fontWeight: 700,
                                px: 2,
                                py: 0.7,
                                ml: 1,
                                fontSize: "0.78rem",
                                borderRadius: "24px",
                                boxShadow: "0 3px 8px rgba(255,255,255,0.35)",
                                "&:hover": {
                                    background: "#e4e0ff",
                                    boxShadow: "0 4px 12px rgba(255,255,255,0.45)",
                                },
                            }}
                        >
                            {PageLang?.commonRegisterButtom[language]}
                        </Button>
                        <Box>
                            <Button
                                endIcon={<LoginIcon sx={{ mr: 1 }} />}
                                onClick={() => navigate(NAVIGATE_AUTH.LOGIN_PAGE)}
                                variant="contained"
                                sx={{
                                    background: "#fff",
                                    color: "primary.main",
                                    fontWeight: 700,
                                    px: 2,
                                    py: 0.7,
                                    ml: 1,
                                    fontSize: "0.78rem",
                                    borderRadius: "24px",
                                    boxShadow: "0 3px 8px rgba(255,255,255,0.35)",
                                    "&:hover": {
                                        background: "#e4e0ff",
                                        boxShadow: "0 4px 12px rgba(255,255,255,0.45)",
                                    },
                                }}
                            >
                                {PageLang?.loginButtonText[language]}</Button>
                        </Box>
                        <RegisterDialog open={open} onClose={() => handleClose()} />
                    </Container>
                </Toolbar>

                <Toolbar sx={{
                    position: "relative",
                    backgroundColor: "common.white",
                    backgroundImage: "url('/rugna-mitra/topbar.svg')",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    boxShadow: "0px 6px 16px rgba(0,0,0,0.10)",
                    backgroundPosition: "center",
                }}>
                    <Container maxWidth="xl" sx={{ zIndex: 1, display: { xs: "block", md: "flex" }, minHeight: "100px !important", border: "none", justifyContent: "space-between", width: "100%", margin: "auto", px: 0, alignItems: "center" }}>
                        <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
                            <NavLink to="https://maharashtra.gov.in" target="_blank">
                                <ImageWithLoader
                                    src="/logo/govt_of_mah.png"
                                    alt="Government Logo"
                                    style={{ width: isMobile ? 70 : 90, height: isMobile ? 70 : 90, cursor: 'pointer' }}
                                />
                            </NavLink>

                            <Box sx={{ borderRight: `2px solid ${theme.palette.primary.main}`, height: { xs: "30px", sm: "60px" }, mx: 2, borderRadius: 3 }} />
                            <Box sx={{ maxWidth: "500px", flexShrink: 1, p: { xs: 1, md: 1 } }}>
                                <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main} sx={{ fontSize: { xs: "1rem", md: "2rem" } }}>{PageLang?.projectTitle[language]}</Typography>
                                <Typography variant="body2" color={theme.palette.text.secondary} sx={{ display: { xs: "1rem", md: "1.8rem" } }}>{PageLang?.projectSubtitle[language]}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: { xs: "block", md: "flex" }, alignItems: 'center' }}>
                            <Box sx={{ display: { xs: "flex", md: "flex" }, justifyContent: { xs: "space-between", md: "flex-end" } }}>
                                <Box sx={{ padding: isMobile ? "5px" : "5px 10px", borderRadius: "8px", display: "flex", alignItems: "center", flexDirection: isMobile ? "column" : "row", textAlign: isMobile ? "center" : "left" }} >
                                    <Typography variant={isMobile ? "h5" : "h2"} sx={{ fontWeight: "bold", color: theme.palette.text.primary, display: "flex", alignItems: "center" }} >
                                        <IconButton color="error" sx={{ animation: `${ringAnimation} 1.5s infinite ease-in-out`, fontSize: isMobile ? "small" : "medium" }} onClick={() => globalThis.location.href = TOLL_FREE_TEL_LINK} >
                                            <Phone fontSize={isMobile ? "small" : "medium"} />
                                        </IconButton>
                                        {PageLang?.TollFreeTitle[language]}
                                        <a href={TOLL_FREE_TEL_LINK} style={{ color: theme.palette.info.main, marginLeft: "8px", textDecoration: "none", fontWeight: "bold", fontSize: isMobile ? "18px" : "20px" }} >
                                            {PageLang?.TollFreeNumber[language]}
                                        </a>
                                    </Typography>
                                </Box>
                                {isMobile && <ImageWithLoader src="/logo/logo-main.jpeg" alt="Mukhyamantri Rugna Mitra Logo" style={{ width: 60, height: 60, cursor: 'pointer' }} onClick={() => navigate("/")} />}
                                <ImageWithLoader src="/logo/satyamev_jayte.png" alt="Government Logo" style={{ width: isMobile ? 70 : 90, height: isMobile ? 70 : 90, cursor: 'pointer' }} onClick={() => navigate("/")} />
                            </Box>
                        </Box>
                    </Container>
                </Toolbar>
            </AppBar>
        </Box>
    );
}